/**
 * Gli ultimi giudizi su **questo** titolo, e come sono andati (SPEC-023 §6).
 *
 * La spec lo chiama «il posto di maggior valore», ed è vero per una ragione
 * precisa: è il contesto in cui si sta per decidere qualcosa su *quel* titolo.
 * Un'accuratezza aggregata al 62% non aiuta a decidere su ACME; sapere che su
 * ACME gli ultimi tre `Buy` sono finiti tutti sotto la mediana dei pari, sì.
 *
 * ⚠️ **Assente in silenzio quando non c'è niente da dire.** Una scheda con un
 * riquadro vuoto «Esiti dei giudizi» insegna a ignorare quel riquadro, e quando
 * poi si riempirà nessuno lo guarderà più.
 */

import { Card, Spinner, TableWrap } from '@/ui';
import { formatDate } from '@/lib/format';
import { useInstrumentOutcomes, type InstrumentOutcome } from './api';
import { OutcomeBadges } from './OutcomeBadges';
import './outcomes.css';

const SPECIES_LABELS: Record<string, string> = {
  instrument_analysis: 'Analisi di dettaglio',
  daily_briefing_item: 'Digest di portafoglio',
  daily_pick_result: 'Titolo del giorno',
  auto_trade_proposal: 'Verifica di auto-trading',
  value_trigger: 'Scatto di valore',
};

/** Un giudizio è (specie, soggetto, giorno); i suoi esiti sono i tre orizzonti. */
function groupJudgements(outcomes: InstrumentOutcome[]) {
  const groups = new Map<string, { head: InstrumentOutcome; outcomes: InstrumentOutcome[] }>();
  for (const outcome of outcomes) {
    const key = `${outcome.subjectType}:${outcome.subjectId}`;
    const group = groups.get(key) ?? { head: outcome, outcomes: [] };
    group.outcomes.push(outcome);
    groups.set(key, group);
  }
  return [...groups.values()];
}

export function InstrumentOutcomesCard({ instrumentId }: { instrumentId: string }) {
  const { data, isPending, error } = useInstrumentOutcomes(instrumentId);

  if (isPending) return <Spinner label="Carico gli esiti dei giudizi…" />;
  // ⚠️ In errore non si mostra un riquadro rotto: questa scheda è un
  // arricchimento, e non deve poter rovinare la pagina del titolo. Anche una
  // risposta di forma inattesa cade qui: `data.outcomes` si legge in modo
  // difensivo perché un guasto di questo riquadro non deve poter far sparire il
  // grafico dei prezzi che gli sta sopra.
  const outcomes = data?.outcomes ?? [];
  if (error || outcomes.length === 0) return null;

  return (
    <Card
      title="Giudizi passati su questo titolo"
      subtitle="Come sono andati, agli orizzonti misurati. Sono misure, non previsioni."
      flush
    >
      <TableWrap>
        <table className="table table--compact">
          <thead>
            <tr>
              <th>Quando</th>
              <th>Chi</th>
              <th>Giudizio</th>
              <th>Esito</th>
              <th className="col--tertiary">Modello</th>
            </tr>
          </thead>
          <tbody>
            {groupJudgements(outcomes).map((group) => (
              <tr key={`${group.head.subjectType}:${group.head.subjectId}`}>
                <td>{formatDate(group.head.judgedAt)}</td>
                <td>{SPECIES_LABELS[group.head.subjectType] ?? group.head.subjectType}</td>
                <td className="outcome-row__what">{group.head.judgement}</td>
                <td>
                  <OutcomeBadges outcomes={group.outcomes} />
                  {group.outcomes.find((o) => o.status === 'unmeasurable')?.unmeasurableReason && (
                    <div className="outcome-row__reason">
                      {group.outcomes.find((o) => o.status === 'unmeasurable')?.unmeasurableReason}
                    </div>
                  )}
                </td>
                <td className="col--tertiary">
                  {group.head.llmModel ?? <span className="cell--minor">non registrato</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
    </Card>
  );
}
